/**
 * @author        yontaek@zumper.com (Yon Taek Chung)
 * @copyright     Copyright (c) 2022, Zumper
 * @description   HomepageHeaderMobile
 */

import { useColorModeValue, useTheme } from '@chakra-ui/react'
import { HamburgerCloseButton, LinkButton } from '@zcode-ui/controls'
import { Zumper, ZumperBolt } from '@zcode-ui/icons'
import { loadGlobalNavLinkpack } from '@zcode-ui/web-zumper-global-state'
import { PAGE_NAMES } from '@zcode/mixpanel'
import { globalNavLinkpack } from '@zcode/nr/es/shared/api'
import React, { Fragment, startTransition, useState } from 'react'
import { useDispatch } from 'react-redux'
import { TRACK_NAVBAR_ACTION, TRACK_USER_MENU_ACTION } from '../../constants'
import { useUserMenuContext } from '../../module/context/UserMenuContext'
import type { HomepageHeaderMobileProps } from '../../types'
import { MobileMenu } from '../MobileMenu'
import { SearchForm } from '../SearchForm'
import * as styles from './style'

export const HomepageHeaderMobile = ({
  dispatchMixpanelAutocompleteResultClick,
  hideMobileAutocomplete,
  isCurrentSearchView,
  isDetailView,
  isHomepageView,
  isMessage3,
  onLoginClick,
  onLogoutClick,
  onNavLinkClick,
  onSelectAutocompleteResult,
  onSignupClick,
  showMobileAutocomplete,
  signInHref,
  signUpHref,
}: HomepageHeaderMobileProps) => {
  const { dispatchTrackUserMenuClick } = useUserMenuContext()
  const [showMenu, setShowMenu] = useState(false)
  const { colors } = useTheme()
  const burgerColor = useColorModeValue(colors.gray['950'], colors.pink['600'])
  const dispatch = useDispatch()

  const handleClickHamburger = () => {
    startTransition(() => {
      setShowMenu(true)
      dispatch(loadGlobalNavLinkpack({ api: globalNavLinkpack }))
      dispatchTrackUserMenuClick(TRACK_USER_MENU_ACTION.MENU_OPEN)
    })
  }

  return (
    <div id="mobileHeaderHomepage">
      <nav css={styles.homepageNavbar}>
        <div css={styles.HomepageNavSection}>
          <Fragment>
            {!isHomepageView && (
              <LinkButton
                aria-label="Zumper Homepage"
                href={'/'}
                onClick={() => onNavLinkClick?.({ linkClicked: TRACK_NAVBAR_ACTION.LOGO })}
              >
                <ZumperBolt bodyFill={'#0D2A3A'} size={36} />
              </LinkButton>
            )}
            <HamburgerCloseButton
              _hover={{
                background: 'transparent',
              }}
              alignItems="center"
              aria-label={showMenu ? 'close expanded view' : 'open side menu'}
              background="0"
              border="1px solid #ebeae6"
              borderRadius="50%"
              color={burgerColor}
              display="flex"
              height={10}
              justifyContent="center"
              marginLeft={isHomepageView ? 'auto' : 0}
              onClick={handleClickHamburger}
              width={10}
            />
          </Fragment>
        </div>
      </nav>

      {showMobileAutocomplete && (
        <SearchForm
          dispatchMixpanelAutocompleteResultClick={dispatchMixpanelAutocompleteResultClick}
          hideMobileAutocomplete={hideMobileAutocomplete}
          isMobile
          onSelectAutocompleteResult={onSelectAutocompleteResult}
          pageName={PAGE_NAMES.HOMEPAGE}
          placeholder="Search by location or property name"
          showMobileAutocomplete={showMobileAutocomplete}
        />
      )}

      <MobileMenu
        isCurrentSearchView={isCurrentSearchView}
        isDetailView={isDetailView}
        isMessage3={isMessage3}
        isHomepageView={isHomepageView}
        onClose={() => startTransition(() => setShowMenu(false))}
        onLoginClick={onLoginClick}
        onLogoutClick={onLogoutClick}
        onNavLinkClick={onNavLinkClick}
        onSignupClick={onSignupClick}
        showModal={showMenu}
        signInHref={signInHref}
        signUpHref={signUpHref}
      />
    </div>
  )
}
